/**
 * Typed HTTP client for the partnership-iq FastAPI backend.
 *
 * Auth: relies on the session cookie set by /api/auth/verify. All requests
 * include credentials so the cookie flows automatically. On 401 we redirect
 * the browser to /login (preserving the next-URL).
 *
 * All API calls in the app go through this single client.
 */
import type {
  ActivationDecideResponse,
  ActivationDetail,
  ActivationStatus,
  ActivationSummary,
  ActivationSurface,
  AddCreatorBody,
  AddCreatorResponse,
  AuditLogPage,
  BrandDetail,
  BrandSummary,
  BrandWarehouseMappingPatch,
  BrandWarehouseMappingResponse,
  BriefResponse,
  CalibrationResponse,
  CampaignDetail,
  CampaignSummary,
  CaptureNowResponse,
  CreateCampaignBody,
  CreatorDetail,
  CreatorSummary,
  CreatorLeaderboardResponse,
  ActivationRampResponse,
  CreatorCohortsResponse,
  SavedCohortGroup,
  SaveCohortGroupsResult,
  TopHooksResponse,
  CreatorFatigueResponse,
  ContentThemesResponse,
  MessagingThemesResponse,
  DashboardResponse,
  DataFreshness,
  BrandSummaryPayload,
  PerformanceDailyResponse,
  DeliverableResponse,
  DiscoverResponse,
  DiscoveryCandidate,
  EngagementDetail,
  EngagementSummary,
  MemoryListResponse,
  MemoryReadResponse,
  MemoryWriteResult,
  MeResponse,
  MessageResponse,
  MetaConnectionStatus,
  MetaPagesResponse,
  MetaRefreshResponse,
  PostEngagementResponse,
  ProposeCandidatesResponse,
  QualifyResponse,
  ReportEnvelope,
  RequestLinkResponse,
  SettingsResponse,
  SlackTriggerRow,
  StartOutreachResponse,
  VerifyResponse,
  ActionResponse,
  DecideActionBody,
  EditActionBody,
  BulkApproveResponse,
  CruvaConnectionResponse,
  CruvaConnectBody,
  CruvaAutonomyBody,
  CruvaPlaybooksConfigBody,
  CruvaSamplesResponse,
  CruvaSampleDecideBody,
  CruvaActionsEnvelope,
  CruvaInboxResponse,
  CruvaThreadDetail,
  CruvaAutomationsResponse,
  CruvaAutomationChangeBody,
  CruvaWorkflowTemplate,
  CruvaWorkflowFromTemplateBody,
  CruvaWorkflowProposeBody,
  CruvaBriefResponse,
  CruvaPlaybookStatus,
  CruvaRunResponse,
  CruvaPulseResponse,
  ImpactConnectionResponse,
  ImpactCampaign,
  ImpactConnectBody,
  ImpactRun,
  ImpactRunsResponse,
  ImpactPulseResponse,
  ImpactAffiliatesResponse,
  ImpactActionsResponse,
  ImpactPromoCodesResponse,
  ImpactProposeExpireResponse,
  ImpactInquiriesResponse,
  ImpactInvoicesResponse,
  ImpactPolicyResponse,
  ImpactPolicyUpdateBody,
  ImpactTier,
  ImpactTiersResponse,
  ImpactTierUpdateBody,
  ImpactAttributionBody,
  ImpactAttributionResult,
  ImpactAgencyShareResponse,
  AccessResponse,
  MyOrgsResponse,
  SetActiveOrgResponse,
  OrgMemberRow,
  OrgInviteRow,
  OrgBrandRow,
  OrgGrant,
  OrgInviteResult,
  OrgAuditLogRow,
  OrgRole,
  AdminOrg,
  AdminUser,
  CreateOrgResponse,
  AdminAuditRow,
  InviteLanding,
  AcceptInviteResult,
  OnboardBrandBody,
  OnboardBrandResult,
} from './types';
import type {
  ImpactPromotion,
  ImpactPromotionKit,
  ImpactPromotionProposeResponse,
  ImpactPromotionsResponse,
  PromotionCreateBody,
} from './impactPromotions';

/**
 * Turn a FastAPI error body into a readable one-line message. `detail` is a
 * string for HTTPException, but a LIST of {loc, msg} objects for 422
 * validation errors — String() on that yields "[object Object]".
 */
function formatErrorDetail(parsed: unknown): string | null {
  if (!parsed || typeof parsed !== 'object' || !('detail' in parsed)) return null;
  const detail = (parsed as { detail: unknown }).detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map((d) => {
      if (d && typeof d === 'object' && 'msg' in d) {
        const loc = Array.isArray((d as { loc?: unknown[] }).loc)
          ? ((d as { loc: unknown[] }).loc as unknown[]).filter((x) => x !== 'body' && x !== 'query').join('.')
          : '';
        return loc ? `${loc}: ${(d as { msg: string }).msg}` : String((d as { msg: string }).msg);
      }
      return typeof d === 'string' ? d : JSON.stringify(d);
    });
    return parts.join('; ');
  }
  if (detail && typeof detail === 'object') return JSON.stringify(detail);
  return detail == null ? null : String(detail);
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/** When true, ApiError(401) automatically pushes the browser to /login. */
let autoRedirectOn401 = true;

export function setAutoRedirectOn401(enabled: boolean): void {
  autoRedirectOn401 = enabled;
}

function buildLoginUrl(): string {
  const path = window.location.pathname + window.location.search;
  // Don't loop if already on /login.
  if (path.startsWith('/login') || path.startsWith('/auth/verify')) {
    return '/login';
  }
  return `/login?next=${encodeURIComponent(path)}`;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Default true — set false on /api/me check at boot to avoid the redirect loop. */
  redirectOn401?: boolean;
}

async function rawRequest(
  path: string,
  opts: RequestOptions = {},
): Promise<{ res: Response; parsed: unknown }> {
  const { method = 'GET', body, signal, redirectOn401 = true } = opts;

  const headers: Record<string, string> = {};
  let serializedBody: string | undefined;
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    serializedBody = JSON.stringify(body);
  }

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'include',
      headers,
      body: serializedBody,
      signal,
    });
  } catch (err) {
    throw new ApiError(0, `Network error: ${(err as Error).message}`);
  }

  if (res.status === 401 && redirectOn401 && autoRedirectOn401) {
    window.location.assign(buildLoginUrl());
    // Keep the promise pending so callers don't continue rendering.
    return new Promise<{ res: Response; parsed: unknown }>(() => {});
  }

  let parsed: unknown;
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      parsed = await res.json();
    } catch {
      parsed = null;
    }
  } else {
    try {
      parsed = await res.text();
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    const detail = formatErrorDetail(parsed) || res.statusText;
    throw new ApiError(res.status, detail, parsed);
  }

  return { res, parsed };
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { parsed } = await rawRequest(path, opts);
  return parsed as T;
}

/**
 * Like {@link request} but also reads the `X-Total-Count` response header —
 * the true number of rows matching the query's filters before paging. Falls
 * back to the returned array's length when the header is absent, so a caller
 * always gets a sane total.
 */
async function requestWithTotal<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<{ data: T; total: number }> {
  const { res, parsed } = await rawRequest(path, opts);
  const header = res.headers.get('X-Total-Count');
  const parsedTotal = header != null ? parseInt(header, 10) : NaN;
  const total = Number.isFinite(parsedTotal)
    ? parsedTotal
    : Array.isArray(parsed)
      ? parsed.length
      : 0;
  return { data: parsed as T, total };
}

export const api = {
  /* ── Auth ─────────────────────────────────────────────────── */
  me: (): Promise<MeResponse> => request<MeResponse>('/api/me', { redirectOn401: false }),

  requestMagicLink: (email: string): Promise<RequestLinkResponse> =>
    request<RequestLinkResponse>('/api/auth/request-link', {
      method: 'POST',
      body: { email },
      redirectOn401: false,
    }),

  verifyMagicLink: (token: string): Promise<VerifyResponse> =>
    request<VerifyResponse>('/api/auth/verify', {
      method: 'POST',
      body: { token },
      redirectOn401: false,
    }),

  logout: (): Promise<{ ok: boolean }> =>
    request<{ ok: boolean }>('/api/auth/logout', { method: 'POST', redirectOn401: false }),

  /* ── Org layer — access / orgs / active-org (spec §5) ─────── */
  // 401 falls through to the global /login?next= redirect (donor parity: a
  // genuine expired-cookie 401 bounces to login; a pre-backend 404 does not).
  getAccess: (): Promise<AccessResponse> => request<AccessResponse>('/api/me/access'),

  listMyOrgs: (): Promise<MyOrgsResponse> => request<MyOrgsResponse>('/api/me/orgs'),

  setActiveOrg: (orgId: string): Promise<SetActiveOrgResponse> =>
    request<SetActiveOrgResponse>('/api/me/active-org', {
      method: 'POST',
      body: { org_id: orgId },
    }),

  /* ── Brands ───────────────────────────────────────────────── */
  // Portfolio rollup shape (spec §5). `days` clamps the rollup window;
  // `acrossOrgs` (SuperAdmin only, audited bypass) tags each row with its
  // owning org. The bare `listBrands()` call keeps working — both params are
  // optional and the response is the same array shape.
  listBrands: (days?: number, acrossOrgs?: boolean): Promise<BrandSummary[]> => {
    const qs = new URLSearchParams();
    if (days !== undefined) qs.set('days', String(days));
    if (acrossOrgs) qs.set('across_orgs', 'true');
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return request<BrandSummary[]>(`/api/brands${tail}`);
  },

  getBrand: (slug: string): Promise<BrandDetail> =>
    request<BrandDetail>(`/api/brands/${encodeURIComponent(slug)}`),

  patchBrandWarehouseMapping: (
    slug: string,
    body: BrandWarehouseMappingPatch,
  ): Promise<BrandWarehouseMappingResponse> =>
    request<BrandWarehouseMappingResponse>(
      `/api/brands/${encodeURIComponent(slug)}/warehouse-mapping`,
      { method: 'PATCH', body },
    ),

  /* ── Plan 3: Discover ─────────────────────────────────────── */
  discoverTagged: (
    brandSlug: string,
    params: { days?: number; minEngagement?: number; limit?: number } = {},
  ): Promise<DiscoverResponse> => {
    const qs = new URLSearchParams();
    if (params.days !== undefined) qs.set('days', String(params.days));
    if (params.minEngagement !== undefined) qs.set('min_engagement', String(params.minEngagement));
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return request<DiscoverResponse>(
      `/api/brands/${encodeURIComponent(brandSlug)}/discover/tagged${tail}`,
    );
  },

  // Posture B Scene 9 reframe (2026-05-20): /discover/engaged returns
  // per-post comment activity (PostEngagementResponse) — not a candidate
  // list — because IG Graph returns username=NULL for non-business
  // commenters on own media. The `min_comments` filter was removed; the
  // surface is now "Post comments", sorted by comment_count desc.
  discoverEngaged: (
    brandSlug: string,
    params: { days?: number; limit?: number } = {},
  ): Promise<PostEngagementResponse> => {
    const qs = new URLSearchParams();
    if (params.days !== undefined) qs.set('days', String(params.days));
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return request<PostEngagementResponse>(
      `/api/brands/${encodeURIComponent(brandSlug)}/discover/engaged${tail}`,
    );
  },

  discoverMarketplace: (
    brandSlug: string,
    params: {
      q?: string;
      minFollowers?: number;
      maxFollowers?: number;
      country?: string;
      sort?: string;
      order?: 'asc' | 'desc';
      limit?: number;
    } = {},
  ): Promise<DiscoverResponse> => {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.minFollowers !== undefined) qs.set('min_followers', String(params.minFollowers));
    if (params.maxFollowers !== undefined) qs.set('max_followers', String(params.maxFollowers));
    if (params.country) qs.set('country', params.country);
    if (params.sort) qs.set('sort', params.sort);
    if (params.order) qs.set('order', params.order);
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return request<DiscoverResponse>(
      `/api/brands/${encodeURIComponent(brandSlug)}/discover/marketplace${tail}`,
    );
  },

  discoverCompetitor: (
    brandSlug: string,
    params: { competitorHandle?: string; limit?: number } = {},
  ): Promise<DiscoverResponse> => {
    const qs = new URLSearchParams();
    if (params.competitorHandle) qs.set('competitor_handle', params.competitorHandle);
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return request<DiscoverResponse>(
      `/api/brands/${encodeURIComponent(brandSlug)}/discover/competitor${tail}`,
    );
  },

  proposeCandidates: (
    brandSlug: string,
    candidates: DiscoveryCandidate[],
  ): Promise<ProposeCandidatesResponse> =>
    request<ProposeCandidatesResponse>(
      `/api/brands/${encodeURIComponent(brandSlug)}/discover/propose`,
      { method: 'POST', body: { candidates } },
    ),

  /* ── Plan 3: Creators ─────────────────────────────────────── */
  listCreators: (
    brandSlug: string,
    params: {
      status?: string;
      q?: string;
      platform?: string;
      sort?: string;
      order?: 'asc' | 'desc';
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<{ data: CreatorSummary[]; total: number }> => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.q) qs.set('q', params.q);
    if (params.platform) qs.set('platform', params.platform);
    if (params.sort) qs.set('sort', params.sort);
    if (params.order) qs.set('order', params.order);
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.offset !== undefined) qs.set('offset', String(params.offset));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return requestWithTotal<CreatorSummary[]>(
      `/api/brands/${encodeURIComponent(brandSlug)}/creators${tail}`,
    );
  },

  getCreator: (brandSlug: string, handle: string): Promise<CreatorDetail> =>
    request<CreatorDetail>(
      `/api/brands/${encodeURIComponent(brandSlug)}/creators/${encodeURIComponent(handle)}`,
    ),

  qualifyCreator: (brandSlug: string, handle: string): Promise<QualifyResponse> =>
    request<QualifyResponse>(
      `/api/brands/${encodeURIComponent(brandSlug)}/creators/${encodeURIComponent(handle)}/qualify`,
      { method: 'POST', body: {} },
    ),

  /* ── Plan 4: Campaigns ────────────────────────────────────── */
  // Server-paged campaigns list (limit/offset + X-Total-Count, optional ?q).
  // Returns {data,total} like listCreators/listActions — one endpoint, one
  // client contract. The endpoint still responds with a bare array (chat/MCP
  // callers unchanged); requestWithTotal reads the X-Total-Count header.
  listCampaigns: (
    brandSlug: string,
    params: {
      q?: string;
      sort?: string;
      order?: 'asc' | 'desc';
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<{ data: CampaignSummary[]; total: number }> => {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.sort) qs.set('sort', params.sort);
    if (params.order) qs.set('order', params.order);
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.offset !== undefined) qs.set('offset', String(params.offset));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return requestWithTotal<CampaignSummary[]>(
      `/api/brands/${encodeURIComponent(brandSlug)}/campaigns${tail}`,
    );
  },

  getCampaign: (brandSlug: string, slug: string): Promise<CampaignDetail> =>
    request<CampaignDetail>(
      `/api/brands/${encodeURIComponent(brandSlug)}/campaigns/${encodeURIComponent(slug)}`,
    ),

  createCampaign: (
    brandSlug: string,
    body: CreateCampaignBody,
  ): Promise<CampaignDetail> =>
    request<CampaignDetail>(
      `/api/brands/${encodeURIComponent(brandSlug)}/campaigns`,
      { method: 'POST', body },
    ),

  addCreatorToCampaign: (
    brandSlug: string,
    campaignSlug: string,
    body: AddCreatorBody,
  ): Promise<AddCreatorResponse> =>
    request<AddCreatorResponse>(
      `/api/brands/${encodeURIComponent(brandSlug)}/campaigns/${encodeURIComponent(campaignSlug)}/creators`,
      { method: 'POST', body },
    ),

  /* ── Plan 4: Engagements ──────────────────────────────────── */
  listEngagements: (
    brandSlug: string,
    params: {
      state?: string;
      campaignId?: string;
      sort?: string;
      order?: 'asc' | 'desc';
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<EngagementSummary[]> => {
    const qs = new URLSearchParams();
    if (params.state) qs.set('state', params.state);
    if (params.campaignId) qs.set('campaign_id', params.campaignId);
    if (params.sort) qs.set('sort', params.sort);
    if (params.order) qs.set('order', params.order);
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.offset !== undefined) qs.set('offset', String(params.offset));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return request<EngagementSummary[]>(
      `/api/brands/${encodeURIComponent(brandSlug)}/engagements${tail}`,
    );
  },

  getEngagement: (brandSlug: string, engagementId: string): Promise<EngagementDetail> =>
    request<EngagementDetail>(
      `/api/brands/${encodeURIComponent(brandSlug)}/engagements/${encodeURIComponent(engagementId)}`,
    ),

  startOutreach: (
    brandSlug: string,
    engagementId: string,
    intent = 'first_contact',
  ): Promise<StartOutreachResponse> =>
    request<StartOutreachResponse>(
      `/api/brands/${encodeURIComponent(brandSlug)}/engagements/${encodeURIComponent(engagementId)}/outreach/start`,
      { method: 'POST', body: { intent } },
    ),

  approveMessage: (messageId: string): Promise<MessageResponse> =>
    request<MessageResponse>(
      `/api/messages/${encodeURIComponent(messageId)}/approve`,
      { method: 'POST', body: {} },
    ),

  editSendMessage: (
    messageId: string,
    edits: { subject?: string; body_markdown?: string },
  ): Promise<MessageResponse> =>
    request<MessageResponse>(
      `/api/messages/${encodeURIComponent(messageId)}/edit_send`,
      { method: 'POST', body: edits },
    ),

  rejectMessage: (messageId: string): Promise<MessageResponse> =>
    request<MessageResponse>(
      `/api/messages/${encodeURIComponent(messageId)}/reject`,
      { method: 'POST', body: {} },
    ),

  /* ── Plan 4: Briefs ───────────────────────────────────────── */
  listBriefs: (brandSlug: string): Promise<BriefResponse[]> =>
    request<BriefResponse[]>(
      `/api/brands/${encodeURIComponent(brandSlug)}/briefs`,
    ),

  getBrief: (brandSlug: string, briefId: string): Promise<BriefResponse> =>
    request<BriefResponse>(
      `/api/brands/${encodeURIComponent(brandSlug)}/briefs/${encodeURIComponent(briefId)}`,
    ),

  regenerateBrief: (brandSlug: string, engagementId: string): Promise<BriefResponse> =>
    request<BriefResponse>(
      `/api/brands/${encodeURIComponent(brandSlug)}/engagements/${encodeURIComponent(engagementId)}/brief/regenerate`,
      { method: 'POST', body: {} },
    ),

  /* ── Plan 5: Activations ──────────────────────────────────── */
  listActivations: (
    brandSlug: string,
    params: { surface?: ActivationSurface; status?: ActivationStatus; limit?: number; offset?: number } = {},
  ): Promise<ActivationSummary[]> => {
    const qs = new URLSearchParams();
    if (params.surface) qs.set('surface', params.surface);
    if (params.status) qs.set('status', params.status);
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.offset !== undefined) qs.set('offset', String(params.offset));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return request<ActivationSummary[]>(
      `/api/brands/${encodeURIComponent(brandSlug)}/activations${tail}`,
    );
  },

  getActivation: (activationId: string): Promise<ActivationDetail> =>
    request<ActivationDetail>(`/api/activations/${encodeURIComponent(activationId)}`),

  approveActivation: (activationId: string, reason?: string): Promise<ActivationDecideResponse> =>
    request<ActivationDecideResponse>(
      `/api/activations/${encodeURIComponent(activationId)}/approve`,
      { method: 'POST', body: { reason } },
    ),

  rejectActivation: (activationId: string, reason?: string): Promise<ActivationDecideResponse> =>
    request<ActivationDecideResponse>(
      `/api/activations/${encodeURIComponent(activationId)}/reject`,
      { method: 'POST', body: { reason } },
    ),

  /* ── Plan 5: Deliverables ─────────────────────────────────── */
  listDeliverables: (
    brandSlug: string,
    params: { campaignId?: string; engagementId?: string; limit?: number; offset?: number } = {},
  ): Promise<DeliverableResponse[]> => {
    const qs = new URLSearchParams();
    if (params.campaignId) qs.set('campaign_id', params.campaignId);
    if (params.engagementId) qs.set('engagement_id', params.engagementId);
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.offset !== undefined) qs.set('offset', String(params.offset));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return request<DeliverableResponse[]>(
      `/api/brands/${encodeURIComponent(brandSlug)}/deliverables${tail}`,
    );
  },

  captureDeliverableNow: (
    brandSlug: string,
    deliverableId: string,
    url: string,
  ): Promise<CaptureNowResponse> =>
    request<CaptureNowResponse>(
      `/api/brands/${encodeURIComponent(brandSlug)}/deliverables/${encodeURIComponent(deliverableId)}/capture-now`,
      { method: 'POST', body: { url } },
    ),

  /* ── Plan 7: Dashboard ────────────────────────────────────── */
  getDashboard: (
    brandSlug: string,
    params: {
      windowDays?: number;
      useCache?: boolean;
      // ads-iq parity (S2): explicit window + compare mode. `from`/`to` are
      // YYYY-MM-DD (inclusive); both required together. window_days still works.
      from?: string;
      to?: string;
      compare?: string;
    } = {},
  ): Promise<DashboardResponse> => {
    const qs = new URLSearchParams();
    if (params.windowDays !== undefined) qs.set('window_days', String(params.windowDays));
    if (params.useCache !== undefined) qs.set('use_cache', String(params.useCache));
    if (params.from !== undefined) qs.set('from', params.from);
    if (params.to !== undefined) qs.set('to', params.to);
    if (params.compare !== undefined) qs.set('compare', params.compare);
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return request<DashboardResponse>(
      `/api/brands/${encodeURIComponent(brandSlug)}/dashboard${tail}`,
    );
  },

  /* ── P053: data-freshness pill (ads-iq DataFreshnessPill parity) ── */
  dataFreshness: (slug: string): Promise<DataFreshness> =>
    request<DataFreshness>(
      `/api/brands/${encodeURIComponent(slug)}/data-freshness`,
    ),

  /* ── S2: per-channel blended daily for the Performance page ── */
  performanceDaily: (
    brandSlug: string,
    params: { windowDays?: number } = {},
  ): Promise<PerformanceDailyResponse> => {
    const qs = new URLSearchParams();
    if (params.windowDays !== undefined) qs.set('days', String(params.windowDays));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return request<PerformanceDailyResponse>(
      `/api/brands/${encodeURIComponent(brandSlug)}/performance/daily${tail}`,
    );
  },

  /* ── S2: cross-channel Brand Summary (ads-iq BrandSummary xerox) ── */
  brandSummary: (
    brandSlug: string,
    days: number,
  ): Promise<BrandSummaryPayload> => {
    const qs = new URLSearchParams();
    qs.set('days', String(days));
    return request<BrandSummaryPayload>(
      `/api/brands/${encodeURIComponent(brandSlug)}/summary?${qs.toString()}`,
    );
  },

  /* ── Plan 7: Reports ──────────────────────────────────────── */
  reportCreatorsActivatedByMonth: (
    brandSlug: string,
    params: { from?: string; to?: string; windowDays?: number } = {},
  ): Promise<ReportEnvelope> => {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.windowDays !== undefined) qs.set('window_days', String(params.windowDays));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return request<ReportEnvelope>(
      `/api/brands/${encodeURIComponent(brandSlug)}/reports/creators-activated-by-month${tail}`,
    );
  },

  reportSpendBySurface: (
    brandSlug: string,
    params: { from?: string; to?: string; windowDays?: number } = {},
  ): Promise<ReportEnvelope> => {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.windowDays !== undefined) qs.set('window_days', String(params.windowDays));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return request<ReportEnvelope>(
      `/api/brands/${encodeURIComponent(brandSlug)}/reports/spend-by-surface${tail}`,
    );
  },

  reportRoasBySurface: (
    brandSlug: string,
    params: { from?: string; to?: string; windowDays?: number } = {},
  ): Promise<ReportEnvelope> => {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.windowDays !== undefined) qs.set('window_days', String(params.windowDays));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return request<ReportEnvelope>(
      `/api/brands/${encodeURIComponent(brandSlug)}/reports/roas-by-surface${tail}`,
    );
  },

  reportPredictedVsActual: (
    brandSlug: string,
    params: { from?: string; to?: string; windowDays?: number; limit?: number } = {},
  ): Promise<CalibrationResponse> => {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.windowDays !== undefined) qs.set('window_days', String(params.windowDays));
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return request<CalibrationResponse>(
      `/api/brands/${encodeURIComponent(brandSlug)}/reports/predicted-vs-actual${tail}`,
    );
  },

  /* ── Plan 7: Brand Memory ─────────────────────────────────── */
  listMemory: (brandSlug: string): Promise<MemoryListResponse> =>
    request<MemoryListResponse>(`/api/brands/${encodeURIComponent(brandSlug)}/memory`),

  readMemory: (brandSlug: string, fileName: string): Promise<MemoryReadResponse> =>
    request<MemoryReadResponse>(
      `/api/brands/${encodeURIComponent(brandSlug)}/memory/${fileName}`,
    ),

  writeMemory: (
    brandSlug: string,
    fileName: string,
    body: { content: string; expected_mtime_ns: number | null },
  ): Promise<MemoryWriteResult> =>
    request<MemoryWriteResult>(
      `/api/brands/${encodeURIComponent(brandSlug)}/memory/${fileName}`,
      { method: 'PUT', body },
    ),

  /* ── Plan 7: Settings ─────────────────────────────────────── */
  getSettings: (brandSlug: string): Promise<SettingsResponse> =>
    request<SettingsResponse>(`/api/brands/${encodeURIComponent(brandSlug)}/settings`),

  updateBrandInfo: (
    brandSlug: string,
    body: { name?: string | null; timezone?: string | null },
  ): Promise<{ ok: boolean }> =>
    request<{ ok: boolean }>(
      `/api/brands/${encodeURIComponent(brandSlug)}/settings/brand-info`,
      { method: 'PUT', body },
    ),

  updateIntegration: (
    brandSlug: string,
    body: { provider: string; secret_value?: string | null; notes?: string | null },
  ): Promise<{ ok: boolean; provider: string; configured: boolean }> =>
    request(`/api/brands/${encodeURIComponent(brandSlug)}/settings/integration`, {
      method: 'PUT',
      body,
    }),

  updateStopConditions: (
    brandSlug: string,
    overrides: Record<string, unknown>,
  ): Promise<{ ok: boolean; effective: Record<string, unknown> }> =>
    request(`/api/brands/${encodeURIComponent(brandSlug)}/settings/stop-conditions`, {
      method: 'PUT',
      body: { overrides },
    }),

  updateSlackTriggers: (
    brandSlug: string,
    triggers: SlackTriggerRow[],
  ): Promise<{ ok: boolean }> =>
    request(`/api/brands/${encodeURIComponent(brandSlug)}/settings/slack-triggers`, {
      method: 'PUT',
      body: { triggers },
    }),

  // Brand-scoped user invite / role routes REMOVED (spec §5) — org membership
  // now lives at the org level (Org settings → Members). See api.org.*.

  /* ── Plan 7: Audit Log ────────────────────────────────────── */
  listAuditLog: (
    brandSlug: string,
    params: {
      from?: string;
      to?: string;
      actorType?: string;
      action?: string;
      objectType?: string;
      userId?: string;
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<AuditLogPage> => {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    if (params.actorType) qs.set('actor_type', params.actorType);
    if (params.action) qs.set('action', params.action);
    if (params.objectType) qs.set('object_type', params.objectType);
    if (params.userId) qs.set('user_id', params.userId);
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.offset !== undefined) qs.set('offset', String(params.offset));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return request<AuditLogPage>(
      `/api/brands/${encodeURIComponent(brandSlug)}/audit-log${tail}`,
    );
  },

  /* ── Plan 7: Chat (SSE) ───────────────────────────────────── */
  chatStreamUrl: (brandSlug: string): string =>
    `/api/brands/${encodeURIComponent(brandSlug)}/chat/stream`,

  /* ── Plan 8: Meta integration (Page picker + connection) ──── */
  getMetaConnection: (brandSlug: string): Promise<MetaConnectionStatus> =>
    request<MetaConnectionStatus>(
      `/api/brands/${encodeURIComponent(brandSlug)}/meta/connection`,
    ),

  listMetaPages: (brandSlug: string): Promise<MetaPagesResponse> =>
    request<MetaPagesResponse>(
      `/api/brands/${encodeURIComponent(brandSlug)}/meta/pages`,
    ),

  selectMetaPage: (brandSlug: string, pageId: string): Promise<{ ok: boolean }> =>
    request<{ ok: boolean }>(
      `/api/brands/${encodeURIComponent(brandSlug)}/meta/pages/${encodeURIComponent(pageId)}/select`,
      { method: 'POST', body: {} },
    ),

  disconnectMeta: (brandSlug: string): Promise<{ ok: boolean }> =>
    request<{ ok: boolean }>(
      `/api/brands/${encodeURIComponent(brandSlug)}/meta/disconnect`,
      { method: 'POST', body: {} },
    ),

  metaStartUrl: (brandSlug: string, nextPath?: string): string => {
    const qs = new URLSearchParams({ brand_slug: brandSlug });
    if (nextPath) qs.set('next', nextPath);
    return `/api/auth/meta/start?${qs.toString()}`;
  },

  /* ── Plan 9 — MetaConnect page (refresh + connect helper) ─────── */
  /**
   * Plan 9 Task 9: kick off the OAuth Page-picker flow. We don't actually
   * round-trip to the backend here because /api/auth/meta/start IS a 302
   * redirect; instead, return the consent URL the page should assign to
   * window.location. Mirrors how Settings → MetaIntegration uses
   * `metaStartUrl(...)`, just shaped as a Promise so the page can await it
   * inside a `useMutation` (matches the contract in plan 9 task 9 spec).
   */
  startMetaConnect: (
    brandSlug: string,
    nextPath?: string,
  ): Promise<{ consent_url: string }> =>
    Promise.resolve({
      consent_url: api.metaStartUrl(brandSlug, nextPath),
    }),

  /**
   * Plan 9 Task 9: trigger an on-demand IG ingestion refresh for the
   * current operator's connection on this brand. Server-debounced to
   * once per 5 minutes — a `throttled` response is NOT an error, the
   * page surfaces it as a soft toast with `wait_seconds`.
   */
  refreshMetaConnection: (brandSlug: string): Promise<MetaRefreshResponse> =>
    request<MetaRefreshResponse>(
      `/api/brands/${encodeURIComponent(brandSlug)}/meta/refresh`,
      { method: 'POST', body: {} },
    ),

  /* ── Plan 2/6: Action Queue ───────────────────────────────── */
  listActions: (
    brandSlug: string,
    params: { status?: string; type?: string; limit?: number; offset?: number } = {},
  ): Promise<{ data: ActionResponse[]; total: number }> => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.type) qs.set('type', params.type);
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.offset !== undefined) qs.set('offset', String(params.offset));
    const tail = qs.toString() ? `?${qs.toString()}` : '';
    return requestWithTotal<ActionResponse[]>(
      `/api/brands/${encodeURIComponent(brandSlug)}/actions${tail}`,
    );
  },

  listActionTypes: (brandSlug: string): Promise<string[]> =>
    request<string[]>(
      `/api/brands/${encodeURIComponent(brandSlug)}/actions/types`,
    ),

  approveAction: (actionId: string, body: DecideActionBody = {}): Promise<ActionResponse> =>
    request<ActionResponse>(`/api/actions/${encodeURIComponent(actionId)}/approve`, {
      method: 'POST',
      body,
    }),

  rejectAction: (actionId: string, body: DecideActionBody = {}): Promise<ActionResponse> =>
    request<ActionResponse>(`/api/actions/${encodeURIComponent(actionId)}/reject`, {
      method: 'POST',
      body,
    }),

  deferAction: (actionId: string, body: DecideActionBody): Promise<ActionResponse> =>
    request<ActionResponse>(`/api/actions/${encodeURIComponent(actionId)}/defer`, {
      method: 'POST',
      body,
    }),

  retryAction: (actionId: string): Promise<ActionResponse> =>
    request<ActionResponse>(`/api/actions/${encodeURIComponent(actionId)}/retry`, {
      method: 'POST',
      body: {},
    }),

  /* Edit-First (S3): patch a pending action's title/context before approving. */
  editAction: (actionId: string, body: EditActionBody): Promise<ActionResponse> =>
    request<ActionResponse>(`/api/actions/${encodeURIComponent(actionId)}/edit`, {
      method: 'POST',
      body,
    }),

  bulkApproveActions: (actionIds: string[]): Promise<BulkApproveResponse> =>
    request<BulkApproveResponse>(`/api/actions/bulk-approve`, {
      method: 'POST',
      body: { action_ids: actionIds },
    }),

  /* ── Plan 6: Cruva (TikTok Shop) channel ──────────────────── */
  cruva: {
    getConnection: (brandSlug: string): Promise<CruvaConnectionResponse> =>
      request<CruvaConnectionResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/connection`,
      ),

    putConnection: (
      brandSlug: string,
      body: CruvaConnectBody,
    ): Promise<CruvaConnectionResponse> =>
      request<CruvaConnectionResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/connection`,
        { method: 'PUT', body },
      ),

    deleteConnection: (brandSlug: string): Promise<void> =>
      request<void>(`/api/brands/${encodeURIComponent(brandSlug)}/cruva/connection`, {
        method: 'DELETE',
      }),

    putAutonomy: (
      brandSlug: string,
      body: CruvaAutonomyBody,
    ): Promise<CruvaConnectionResponse> =>
      request<CruvaConnectionResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/connection/autonomy`,
        { method: 'PUT', body },
      ),

    putPlaybooksConfig: (
      brandSlug: string,
      body: CruvaPlaybooksConfigBody,
    ): Promise<CruvaConnectionResponse> =>
      request<CruvaConnectionResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/connection/playbooks`,
        { method: 'PUT', body },
      ),

    sync: (brandSlug: string, scope: string): Promise<CruvaRunResponse> =>
      request<CruvaRunResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/sync`,
        { method: 'POST', body: { scope } },
      ),

    listSamples: (
      brandSlug: string,
      params: {
        status?: string;
        expiringWithinDays?: number;
        q?: string;
        limit?: number;
        offset?: number;
      } = {},
    ): Promise<CruvaSamplesResponse> => {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.expiringWithinDays !== undefined)
        qs.set('expiring_within_days', String(params.expiringWithinDays));
      if (params.q) qs.set('q', params.q);
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      if (params.offset !== undefined) qs.set('offset', String(params.offset));
      const tail = qs.toString() ? `?${qs.toString()}` : '';
      return request<CruvaSamplesResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/samples${tail}`,
      );
    },

    decideSamples: (
      brandSlug: string,
      body: CruvaSampleDecideBody,
    ): Promise<CruvaActionsEnvelope> =>
      request<CruvaActionsEnvelope>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/samples/decide`,
        { method: 'POST', body },
      ),

    listInbox: (
      brandSlug: string,
      params: {
        status?: string;
        classification?: string;
        limit?: number;
        offset?: number;
      } = {},
    ): Promise<CruvaInboxResponse> => {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.classification) qs.set('classification', params.classification);
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      if (params.offset !== undefined) qs.set('offset', String(params.offset));
      const tail = qs.toString() ? `?${qs.toString()}` : '';
      return request<CruvaInboxResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/inbox${tail}`,
      );
    },

    getThread: (brandSlug: string, conversationId: string): Promise<CruvaThreadDetail> =>
      request<CruvaThreadDetail>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/inbox/${encodeURIComponent(conversationId)}`,
      ),

    replyThread: (
      brandSlug: string,
      conversationId: string,
      text: string,
    ): Promise<ActionResponse> =>
      request<ActionResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/inbox/${encodeURIComponent(conversationId)}/reply`,
        { method: 'POST', body: { text } },
      ),

    getAutomations: (brandSlug: string): Promise<CruvaAutomationsResponse> =>
      request<CruvaAutomationsResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/automations`,
      ),

    proposeAutomationChange: (
      brandSlug: string,
      automationId: string,
      body: CruvaAutomationChangeBody,
    ): Promise<ActionResponse> =>
      request<ActionResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/automations/${encodeURIComponent(automationId)}/propose`,
        { method: 'POST', body },
      ),

    getWorkflowTemplates: (brandSlug: string): Promise<CruvaWorkflowTemplate[]> =>
      request<CruvaWorkflowTemplate[]>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/workflows/templates`,
      ),

    createWorkflowFromTemplate: (
      brandSlug: string,
      body: CruvaWorkflowFromTemplateBody,
    ): Promise<ActionResponse> =>
      request<ActionResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/workflows/from-template`,
        { method: 'POST', body },
      ),

    proposeWorkflowChange: (
      brandSlug: string,
      workflowId: string,
      body: CruvaWorkflowProposeBody,
    ): Promise<ActionResponse> =>
      request<ActionResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/workflows/${encodeURIComponent(workflowId)}/propose`,
        { method: 'POST', body },
      ),

    listBriefs: (brandSlug: string): Promise<CruvaBriefResponse[]> =>
      request<CruvaBriefResponse[]>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/briefs`,
      ),

    proposeBriefRefresh: (brandSlug: string, briefId: string): Promise<ActionResponse> =>
      request<ActionResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/briefs/${encodeURIComponent(briefId)}/propose-refresh`,
        { method: 'POST', body: {} },
      ),

    listPlaybooks: (brandSlug: string): Promise<CruvaPlaybookStatus[]> =>
      request<CruvaPlaybookStatus[]>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/playbooks`,
      ),

    runPlaybook: (brandSlug: string, key: string): Promise<CruvaRunResponse> =>
      request<CruvaRunResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/playbooks/${encodeURIComponent(key)}/run`,
        { method: 'POST', body: {} },
      ),

    listRuns: (brandSlug: string, params: { limit?: number } = {}): Promise<CruvaRunResponse[]> => {
      const qs = new URLSearchParams();
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      const tail = qs.toString() ? `?${qs.toString()}` : '';
      return request<CruvaRunResponse[]>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/runs${tail}`,
      );
    },

    getPulse: (brandSlug: string, params: { days?: number } = {}): Promise<CruvaPulseResponse> => {
      const qs = new URLSearchParams();
      if (params.days !== undefined) qs.set('days', String(params.days));
      const tail = qs.toString() ? `?${qs.toString()}` : '';
      return request<CruvaPulseResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/cruva/pulse${tail}`,
      );
    },
  },

  /* ── Plan 4: Impact (warehouse-fed affiliate) channel ─────────
   *  Read-only mirror + connection. Paths under /brands/{slug}/impact,
   *  disjoint from the legacy integrations/impact routes (spec §3.5). */
  impact: {
    getConnection: (brandSlug: string): Promise<ImpactConnectionResponse> =>
      request<ImpactConnectionResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/connection`,
      ),

    putConnection: (
      brandSlug: string,
      body: ImpactConnectBody,
    ): Promise<ImpactConnectionResponse> =>
      request<ImpactConnectionResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/connection`,
        { method: 'PUT', body },
      ),

    deleteConnection: (brandSlug: string): Promise<void> =>
      request<void>(`/api/brands/${encodeURIComponent(brandSlug)}/impact/connection`, {
        method: 'DELETE',
      }),

    listCampaigns: (brandSlug: string): Promise<ImpactCampaign[]> =>
      request<ImpactCampaign[]>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/campaigns`,
      ),

    warm: (brandSlug: string): Promise<ImpactRun> =>
      request<ImpactRun>(`/api/brands/${encodeURIComponent(brandSlug)}/impact/warm`, {
        method: 'POST',
        body: {},
      }),

    listRuns: (brandSlug: string): Promise<ImpactRunsResponse> =>
      request<ImpactRunsResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/runs`,
      ),

    getPulse: (
      brandSlug: string,
      params: { days?: number } = {},
    ): Promise<ImpactPulseResponse> => {
      const qs = new URLSearchParams();
      if (params.days !== undefined) qs.set('days', String(params.days));
      const tail = qs.toString() ? `?${qs.toString()}` : '';
      return request<ImpactPulseResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/pulse${tail}`,
      );
    },

    listAffiliates: (
      brandSlug: string,
      params: {
        days?: number;
        q?: string;
        sort?: string;
        order?: 'asc' | 'desc';
        source?: string;
        paidSearch?: boolean;
        limit?: number;
        offset?: number;
      } = {},
    ): Promise<ImpactAffiliatesResponse> => {
      const qs = new URLSearchParams();
      if (params.days !== undefined) qs.set('days', String(params.days));
      if (params.q) qs.set('q', params.q);
      if (params.sort) qs.set('sort', params.sort);
      if (params.order) qs.set('order', params.order);
      if (params.source) qs.set('source', params.source);
      if (params.paidSearch) qs.set('paid_search', 'true');
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      if (params.offset !== undefined) qs.set('offset', String(params.offset));
      const tail = qs.toString() ? `?${qs.toString()}` : '';
      return request<ImpactAffiliatesResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/affiliates${tail}`,
      );
    },

    listActions: (
      brandSlug: string,
      params: {
        state?: string;
        partner?: string;
        from?: string;
        to?: string;
        promoOnly?: boolean;
        limit?: number;
        offset?: number;
      } = {},
    ): Promise<ImpactActionsResponse> => {
      const qs = new URLSearchParams();
      if (params.state) qs.set('state', params.state);
      if (params.partner) qs.set('partner', params.partner);
      if (params.from) qs.set('from', params.from);
      if (params.to) qs.set('to', params.to);
      if (params.promoOnly) qs.set('promo_only', 'true');
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      if (params.offset !== undefined) qs.set('offset', String(params.offset));
      const tail = qs.toString() ? `?${qs.toString()}` : '';
      return request<ImpactActionsResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/actions${tail}`,
      );
    },

    listPromoCodes: (brandSlug: string): Promise<ImpactPromoCodesResponse> =>
      request<ImpactPromoCodesResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/promo-codes`,
      ),

    /* Action-disciplined expire: mints an OPEN Action; the Impact DELETE runs
       on approval (lane FU-D). Returns the minted action id. */
    proposeExpirePromoCode: (
      brandSlug: string,
      promoCodeIdExternal: string,
    ): Promise<ImpactProposeExpireResponse> =>
      request<ImpactProposeExpireResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/promo-codes/${encodeURIComponent(
          promoCodeIdExternal,
        )}/propose-expire`,
        { method: 'POST', body: {} },
      ),

    listInquiries: (
      brandSlug: string,
      params: { limit?: number; offset?: number } = {},
    ): Promise<ImpactInquiriesResponse> => {
      const qs = new URLSearchParams();
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      if (params.offset !== undefined) qs.set('offset', String(params.offset));
      const tail = qs.toString() ? `?${qs.toString()}` : '';
      return request<ImpactInquiriesResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/inquiries${tail}`,
      );
    },

    invoices: (brandSlug: string): Promise<ImpactInvoicesResponse> =>
      request<ImpactInvoicesResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/invoices`,
      ),

    /* Promotions (Plan 5, spec §3.6): draft → propose (Action) → launch. */
    listPromotions: (
      brandSlug: string,
      params: { status?: string; limit?: number; offset?: number } = {},
    ): Promise<ImpactPromotionsResponse> => {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status_filter', params.status);
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      if (params.offset !== undefined) qs.set('offset', String(params.offset));
      const tail = qs.toString() ? `?${qs.toString()}` : '';
      return request<ImpactPromotionsResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/promotions${tail}`,
      );
    },

    createPromotion: (
      brandSlug: string,
      body: PromotionCreateBody,
    ): Promise<ImpactPromotion> =>
      request<ImpactPromotion>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/promotions`,
        { method: 'POST', body },
      ),

    updatePromotion: (
      brandSlug: string,
      promotionId: string,
      body: Partial<PromotionCreateBody>,
    ): Promise<ImpactPromotion> =>
      request<ImpactPromotion>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/promotions/${encodeURIComponent(promotionId)}`,
        { method: 'PUT', body },
      ),

    proposePromotion: (
      brandSlug: string,
      promotionId: string,
    ): Promise<ImpactPromotionProposeResponse> =>
      request<ImpactPromotionProposeResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/promotions/${encodeURIComponent(promotionId)}/propose`,
        { method: 'POST', body: {} },
      ),

    promotionKit: (
      brandSlug: string,
      promotionId: string,
    ): Promise<ImpactPromotionKit> =>
      request<ImpactPromotionKit>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/promotions/${encodeURIComponent(promotionId)}/kit`,
      ),

    endPromotion: (
      brandSlug: string,
      promotionId: string,
    ): Promise<ImpactPromotion> =>
      request<ImpactPromotion>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/promotions/${encodeURIComponent(promotionId)}/end`,
        { method: 'POST', body: {} },
      ),

    /* Programme policy (affiliate-ops Plan 2). */
    getPolicy: (brandSlug: string): Promise<ImpactPolicyResponse> =>
      request<ImpactPolicyResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/policy`,
      ),

    putPolicy: (
      brandSlug: string,
      body: ImpactPolicyUpdateBody,
    ): Promise<ImpactPolicyResponse> =>
      request<ImpactPolicyResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/policy`,
        { method: 'PUT', body },
      ),

    listTiers: (brandSlug: string): Promise<ImpactTiersResponse> =>
      request<ImpactTiersResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/tiers`,
      ),

    seedTiers: (brandSlug: string): Promise<ImpactTiersResponse> =>
      request<ImpactTiersResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/tiers/seed`,
        { method: 'POST', body: {} },
      ),

    updateTier: (
      brandSlug: string,
      tierId: string,
      body: ImpactTierUpdateBody,
    ): Promise<ImpactTier> =>
      request<ImpactTier>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/tiers/${encodeURIComponent(tierId)}`,
        { method: 'PUT', body },
      ),

    setAttribution: (
      brandSlug: string,
      body: ImpactAttributionBody,
    ): Promise<ImpactAttributionResult> =>
      request<ImpactAttributionResult>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/affiliates/attribution`,
        { method: 'PUT', body },
      ),

    inferAttribution: (brandSlug: string): Promise<ImpactAttributionResult> =>
      request<ImpactAttributionResult>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/affiliates/attribution/infer`,
        { method: 'POST', body: {} },
      ),

    getAgencyShare: (
      brandSlug: string,
      params: { days?: number } = {},
    ): Promise<ImpactAgencyShareResponse> => {
      const qs = new URLSearchParams();
      if (params.days !== undefined) qs.set('days', String(params.days));
      const tail = qs.toString() ? `?${qs.toString()}` : '';
      return request<ImpactAgencyShareResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/impact/affiliates/agency-share${tail}`,
      );
    },
  },

  /* ── Org settings — members / invites / brands / grants / audit ──
   *  (spec §5; require_admin server-side). ─────────────────────── */
  org: {
    listMembers: (): Promise<{ items: OrgMemberRow[] }> =>
      request<{ items: OrgMemberRow[] }>('/api/org/members'),

    patchMember: (userId: string, body: { role: OrgRole }): Promise<{ ok: boolean }> =>
      request<{ ok: boolean }>(`/api/org/members/${encodeURIComponent(userId)}`, {
        method: 'PATCH',
        body,
      }),

    removeMember: (userId: string): Promise<{ ok: boolean }> =>
      request<{ ok: boolean }>(`/api/org/members/${encodeURIComponent(userId)}`, {
        method: 'DELETE',
      }),

    invite: (body: { email: string; role: OrgRole; grants: OrgGrant[] }): Promise<OrgInviteResult> =>
      request<OrgInviteResult>('/api/org/members/invite', { method: 'POST', body }),

    listInvites: (): Promise<{ items: OrgInviteRow[] }> =>
      request<{ items: OrgInviteRow[] }>('/api/org/invites'),

    revokeInvite: (id: string): Promise<{ ok: boolean }> =>
      request<{ ok: boolean }>(`/api/org/invites/${encodeURIComponent(id)}/revoke`, {
        method: 'POST',
      }),

    resendInvite: (id: string): Promise<OrgInviteResult> =>
      request<OrgInviteResult>(`/api/org/invites/${encodeURIComponent(id)}/resend`, {
        method: 'POST',
      }),

    listBrands: (): Promise<{ items: OrgBrandRow[] }> =>
      request<{ items: OrgBrandRow[] }>('/api/org/brands'),

    getMemberGrants: (userId: string): Promise<{ items: OrgGrant[] }> =>
      request<{ items: OrgGrant[] }>(
        `/api/org/members/${encodeURIComponent(userId)}/grants`,
      ),

    putMemberGrants: (userId: string, grants: OrgGrant[]): Promise<{ ok: boolean }> =>
      request<{ ok: boolean }>(`/api/org/members/${encodeURIComponent(userId)}/grants`, {
        method: 'PUT',
        body: { grants },
      }),

    auditLog: (params: {
      eventType?: string;
      since?: string;
      until?: string;
      limit?: number;
      offset?: number;
    } = {}): Promise<{ items: OrgAuditLogRow[]; limit: number; offset: number }> => {
      const qs = new URLSearchParams();
      if (params.eventType) qs.set('event_type', params.eventType);
      if (params.since) qs.set('since', params.since);
      if (params.until) qs.set('until', params.until);
      if (params.limit !== undefined) qs.set('limit', String(params.limit));
      if (params.offset !== undefined) qs.set('offset', String(params.offset));
      const tail = qs.toString() ? `?${qs.toString()}` : '';
      return request(`/api/org/audit-log${tail}`);
    },
  },

  /* ── Admin console — orgs / users / audit (spec §5; SuperAdmin) ── */
  admin: {
    listOrgs: (q?: string): Promise<{ items: AdminOrg[] }> => {
      const tail = q ? `?q=${encodeURIComponent(q)}` : '';
      return request<{ items: AdminOrg[] }>(`/api/admin/orgs${tail}`);
    },

    createOrg: (body: {
      name: string;
      owner_email: string;
      owner_name?: string;
    }): Promise<CreateOrgResponse> =>
      request<CreateOrgResponse>('/api/admin/orgs', { method: 'POST', body }),

    patchOrg: (
      id: string,
      body: { name?: string; new_owner_id?: string },
    ): Promise<{ ok: boolean }> =>
      request<{ ok: boolean }>(`/api/admin/orgs/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body,
      }),

    deleteOrg: (id: string): Promise<{ ok: boolean }> =>
      request<{ ok: boolean }>(`/api/admin/orgs/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),

    listUsers: (q?: string): Promise<{ items: AdminUser[] }> => {
      const tail = q ? `?q=${encodeURIComponent(q)}` : '';
      return request<{ items: AdminUser[] }>(`/api/admin/users${tail}`);
    },

    patchUser: (
      id: string,
      body: { is_admin?: boolean; is_active?: boolean },
    ): Promise<{ ok: boolean }> =>
      request<{ ok: boolean }>(`/api/admin/users/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body,
      }),

    deleteUser: (id: string): Promise<{ ok: boolean }> =>
      request<{ ok: boolean }>(`/api/admin/users/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),

    impersonate: (id: string): Promise<{ ok: boolean }> =>
      request<{ ok: boolean }>(`/api/admin/users/${encodeURIComponent(id)}/impersonate`, {
        method: 'POST',
      }),

    endImpersonation: (): Promise<{ ok: boolean }> =>
      request<{ ok: boolean }>('/api/admin/end-impersonation', { method: 'POST' }),

    audit: (limit = 100): Promise<{ items: AdminAuditRow[] }> =>
      request<{ items: AdminAuditRow[] }>(`/api/admin/audit?limit=${limit}`),
  },

  /* ── Invites (public — no JWT) ────────────────────────────── */
  getInvite: (token: string): Promise<InviteLanding> =>
    request<InviteLanding>(`/api/invites/${encodeURIComponent(token)}`, {
      redirectOn401: false,
    }),

  acceptInvite: (token: string): Promise<AcceptInviteResult> =>
    request<AcceptInviteResult>(`/api/invites/${encodeURIComponent(token)}/accept`, {
      method: 'POST',
      redirectOn401: false,
    }),

  /* ── Onboard — create a brand in the active org (owner/admin) ── */
  onboardBrand: (body: OnboardBrandBody): Promise<OnboardBrandResult> =>
    request<OnboardBrandResult>('/api/onboard/brand', { method: 'POST', body }),

  /* ── Creator Lab (S4 parity, Lane A) — read-only analytics ──── */
  creatorLab: {
    leaderboard: (
      brandSlug: string,
      params: { days?: number; top?: number } = {},
    ): Promise<CreatorLeaderboardResponse> => {
      const qs = new URLSearchParams();
      if (params.days !== undefined) qs.set('days', String(params.days));
      if (params.top !== undefined) qs.set('top', String(params.top));
      const tail = qs.toString() ? `?${qs.toString()}` : '';
      return request<CreatorLeaderboardResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/creator-lab/leaderboard${tail}`,
      );
    },

    activationRamp: (
      brandSlug: string,
      params: {
        launchDays?: number;
        perfDays?: number;
        goalValue?: number;
        scaledThreshold?: number;
      } = {},
    ): Promise<ActivationRampResponse> => {
      const qs = new URLSearchParams();
      if (params.launchDays !== undefined) qs.set('launch_days', String(params.launchDays));
      if (params.perfDays !== undefined) qs.set('perf_days', String(params.perfDays));
      if (params.goalValue !== undefined) qs.set('goal_value', String(params.goalValue));
      if (params.scaledThreshold !== undefined)
        qs.set('scaled_threshold', String(params.scaledThreshold));
      const tail = qs.toString() ? `?${qs.toString()}` : '';
      return request<ActivationRampResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/creator-lab/activation-ramp${tail}`,
      );
    },

    cohorts: (
      brandSlug: string,
      params: { dimension?: string; days?: number } = {},
    ): Promise<CreatorCohortsResponse> => {
      const qs = new URLSearchParams();
      if (params.dimension) qs.set('dimension', params.dimension);
      if (params.days !== undefined) qs.set('days', String(params.days));
      const tail = qs.toString() ? `?${qs.toString()}` : '';
      return request<CreatorCohortsResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/creator-lab/cohorts${tail}`,
      );
    },

    // Replace the brand's saved cohort groups (ads-iq Comparative parity).
    saveCohortGroups: (
      brandSlug: string,
      groups: SavedCohortGroup[],
    ): Promise<SaveCohortGroupsResult> =>
      request<SaveCohortGroupsResult>(
        `/api/brands/${encodeURIComponent(brandSlug)}/creator-lab/cohorts`,
        { method: 'PUT', body: { groups } },
      ),

    topHooks: (
      brandSlug: string,
      params: { days?: number; tone?: string } = {},
    ): Promise<TopHooksResponse> => {
      const qs = new URLSearchParams();
      if (params.days !== undefined) qs.set('days', String(params.days));
      if (params.tone) qs.set('tone', params.tone);
      const tail = qs.toString() ? `?${qs.toString()}` : '';
      return request<TopHooksResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/creator-lab/top-hooks${tail}`,
      );
    },

    fatigue: (
      brandSlug: string,
      params: { days?: number; minVideos?: number } = {},
    ): Promise<CreatorFatigueResponse> => {
      const qs = new URLSearchParams();
      if (params.days !== undefined) qs.set('days', String(params.days));
      if (params.minVideos !== undefined) qs.set('min_videos', String(params.minVideos));
      const tail = qs.toString() ? `?${qs.toString()}` : '';
      return request<CreatorFatigueResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/creator-lab/fatigue${tail}`,
      );
    },

    contentThemes: (
      brandSlug: string,
      params: { days?: number; groupBy?: string } = {},
    ): Promise<ContentThemesResponse> => {
      const qs = new URLSearchParams();
      if (params.days !== undefined) qs.set('days', String(params.days));
      if (params.groupBy) qs.set('group_by', params.groupBy);
      const tail = qs.toString() ? `?${qs.toString()}` : '';
      return request<ContentThemesResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/creator-lab/content-themes${tail}`,
      );
    },

    messagingThemes: (
      brandSlug: string,
      params: { days?: number } = {},
    ): Promise<MessagingThemesResponse> => {
      const qs = new URLSearchParams();
      if (params.days !== undefined) qs.set('days', String(params.days));
      const tail = qs.toString() ? `?${qs.toString()}` : '';
      return request<MessagingThemesResponse>(
        `/api/brands/${encodeURIComponent(brandSlug)}/creator-lab/messaging-themes${tail}`,
      );
    },
  },

  /* ── Generic escape hatch ─────────────────────────────────── */
  raw: <T>(path: string, opts?: RequestOptions): Promise<T> => request<T>(path, opts),
};

export type Api = typeof api;
